/**
 * OutlinerReact.tsx - React wrapper for OutlinerCore with OpCentral orchestrator integration
 * 
 * CHANGE HISTORY:
 * - 251107_2135 [current] danielbeer: feat: Add Interfacr and Profylr integration
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { OutlinerCore } from './OutlinerCore';
import { DISPLAY_MODES, COLUMN_TYPES } from './OutlinerConfig';
import './Outliner.css';

// OpCentral Orchestrator Interfaces (type-only imports to avoid circular deps)
interface InterfacrInstance {
  execute: (params: any) => Promise<any>;
}

interface ProfylrInstance {
  activeProfile?: any;
}

interface OutlinerProps {
  config: any;
  data: any;
  onDataChange?: (data: any) => void;
  // OpCentral Orchestrators
  interfacr?: InterfacrInstance;
  profylr?: ProfylrInstance;
  // Workspace context
  workspaceId?: string;
  datasetId?: string;
}

export const Outliner: React.FC<OutlinerProps> = ({ 
  config, 
  data, 
  onDataChange,
  interfacr,
  profylr,
  workspaceId,
  datasetId
}) => {
  const coreRef = useRef(null);
  const containerRef = useRef(null);
  const [state, setState] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  
  // Orchestrator handlers
  const handleUndo = useCallback(async () => {
    if (interfacr) {
      await interfacr.execute({
        type: 'backtrackr',
        operation: 'undo'
      });
    } else if (config.onUndo) {
      config.onUndo();
    }
  }, [interfacr, config]);
  
  const handleRedo = useCallback(async () => {
    if (interfacr) {
      await interfacr.execute({
        type: 'backtrackr',
        operation: 'redo'
      });
    } else if (config.onRedo) {
      config.onRedo();
    }
  }, [interfacr, config]);
  
  const showNotification = useCallback(async (message: string, variant: 'success' | 'error' | 'info' = 'info') => {
    if (interfacr) {
      await interfacr.execute({
        type: 'notification',
        operation: 'show',
        params: { message, variant }
      });
    }
  }, [interfacr]);
  
  const showConfirmDialog = useCallback(async (title: string, message: string): Promise<boolean> => {
    if (interfacr) {
      return await interfacr.execute({
        type: 'dialog',
        operation: 'confirm',
        params: { title, message, variant: 'warning' }
      });
    }
    return window.confirm(message);
  }, [interfacr]);
  
  // Initialize core
  useEffect(() => {
    coreRef.current = new OutlinerCore({
      ...config,
      data: data || []
    });
    
    // Subscribe to state changes
    const unsubscribe = coreRef.current.subscribe((newState) => {
      setState(newState);
    });
    
    // Initial state
    setState(coreRef.current.getState());
    
    return unsubscribe;
  }, []);
  
  // Update data when prop changes
  // Use ref to prevent infinite loop
  const dataRef = useRef(data);
  useEffect(() => {
    if (coreRef.current && data && data !== dataRef.current) {
      dataRef.current = data;
      coreRef.current.setData(data);
    }
  }, [data]);
  
  // Keyboard navigation
  useEffect(() => {
    console.log('[OutlinerReact] Keyboard navigation setup:', {
      enabled: config.keyboardNavigation,
      hasCore: !!coreRef.current,
      editingCell
    });
    
    if (!config.keyboardNavigation) {
      console.warn('[OutlinerReact] Keyboard navigation is DISABLED in config!');
      return;
    }
    
    const handleKeyDown = async (e) => {
      console.log('[OutlinerReact] KeyDown handler called:', e.key);
      const core = coreRef.current;
      if (!core) {
        console.warn('[OutlinerReact] No core instance!');
        return;
      }
      
      // Don't handle if editing or if target is an input
      if (editingCell || e.target.tagName === 'INPUT') {
        console.log('[OutlinerReact] Ignoring key - editing or input');
        return;
      }
      
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;
      const lastId = core.lastSelectedId;
      
      // Plain arrow navigation
      if (e.key === 'ArrowDown' && !e.shiftKey && !cmdOrCtrl) {
        e.preventDefault();
        e.stopPropagation();
        core.navigateDown();
      } else if (e.key === 'ArrowUp' && !e.shiftKey && !cmdOrCtrl) {
        e.preventDefault();
        e.stopPropagation();
        core.navigateUp();
      } else if (e.key === 'ArrowRight' && !e.shiftKey && !cmdOrCtrl) {
        e.preventDefault();
        if (lastId) {
          // If collapsed, expand. If expanded, navigate to first child
          if (!core.isExpanded(lastId)) {
            core.toggleExpanded(lastId);
          } else {
            core.navigateToFirstChild(lastId);
          }
        }
      } else if (e.key === 'ArrowLeft' && !e.shiftKey && !cmdOrCtrl) {
        e.preventDefault();
        if (lastId) {
          // If expanded, collapse. If collapsed, navigate to parent
          if (core.isExpanded(lastId)) {
            core.toggleExpanded(lastId);
          } else {
            core.navigateToParent(lastId);
          }
        }
      }
      // Selection with Shift
      else if (e.key === 'ArrowDown' && e.shiftKey && !cmdOrCtrl) {
        e.preventDefault();
        const visibleNodes = core.getAllVisibleNodes();
        const currentIndex = visibleNodes.findIndex(n => n[core.config.idField] === lastId);
        if (currentIndex < visibleNodes.length - 1) {
          const nextId = visibleNodes[currentIndex + 1][core.config.idField];
          core.select(nextId, { range: true });
        }
      } else if (e.key === 'ArrowUp' && e.shiftKey && !cmdOrCtrl) {
        e.preventDefault();
        const visibleNodes = core.getAllVisibleNodes();
        const currentIndex = visibleNodes.findIndex(n => n[core.config.idField] === lastId);
        if (currentIndex > 0) {
          const prevId = visibleNodes[currentIndex - 1][core.config.idField];
          core.select(prevId, { range: true });
        }
      }
      // Tree operations
      else if (e.key === 'Tab' && !e.shiftKey) {
        e.preventDefault();
        if (lastId) core.indent(lastId);
      } else if (e.key === 'Tab' && e.shiftKey) {
        e.preventDefault();
        if (lastId) core.outdent(lastId);
      } else if (e.key === 'Enter' && !e.shiftKey && !cmdOrCtrl) {
        console.log('[OutlinerReact] Plain Enter - entering edit mode');
        e.preventDefault();
        if (lastId) {
          const column = core.config.columns.find(c => c.editable);
          if (column) {
            console.log('[OutlinerReact] Setting edit cell:', lastId, column.id);
            setEditingCell({ nodeId: lastId, columnId: column.id });
          } else {
            console.warn('[OutlinerReact] No editable column found!');
          }
        } else {
          console.warn('[OutlinerReact] No item selected (lastId is null)');
        }
      } else if (e.key === 'Enter' && cmdOrCtrl && !e.shiftKey) {
        console.log('[OutlinerReact] Cmd+Enter - creating sibling');
        e.preventDefault();
        if (lastId) {
          console.log('[OutlinerReact] Calling core.addSibling for:', lastId);
          const newId = await core.addSibling(lastId, {});
          console.log('[OutlinerReact] New sibling created:', newId);
          core.select(newId);
          const column = core.config.columns.find(c => c.editable);
          if (column) {
            setTimeout(() => setEditingCell({ nodeId: newId, columnId: column.id }), 0);
          }
        } else {
          console.warn('[OutlinerReact] No item selected for sibling creation');
        }
      } else if (e.key === 'Enter' && e.shiftKey) {
        console.log('[OutlinerReact] Shift+Enter - creating child');
        e.preventDefault();
        if (lastId) {
          console.log('[OutlinerReact] Calling core.addChild for:', lastId);
          const newId = await core.addChild(lastId, {});
          console.log('[OutlinerReact] New child created:', newId);
          core.select(newId);
          const column = core.config.columns.find(c => c.editable);
          if (column) {
            setTimeout(() => setEditingCell({ nodeId: newId, columnId: column.id }), 0);
          }
        } else {
          console.warn('[OutlinerReact] No item selected for child creation');
        }
      }
      // Delete
      else if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        if (lastId) {
          showConfirmDialog('Delete Item', 'Are you sure you want to delete this item?').then(confirmed => {
            if (confirmed) {
              core.deleteNode(lastId);
            }
          });
        }
      }
      // Select all
      else if (cmdOrCtrl && e.key === 'a') {
        e.preventDefault();
        core.getAllVisibleNodes().forEach(node => {
          core.selectedIds.add(node[core.config.idField]);
        });
        core.notify();
      }
      // Escape - Navigate to parent or clear selection
      else if (e.key === 'Escape') {
        e.preventDefault();
        if (lastId) {
          const parentId = core.navigateToParent(lastId);
          if (!parentId) {
            // No parent, clear selection
            core.clearSelection();
          }
        }
      }
      // Space - Enter edit mode
      else if (e.key === ' ' && !cmdOrCtrl) {
        e.preventDefault();
        if (lastId) {
          const column = core.config.columns.find(c => c.editable);
          if (column) {
            setEditingCell({ nodeId: lastId, columnId: column.id });
          }
        }
      }
      // Undo (Cmd+Z)
      else if (cmdOrCtrl && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      // Redo (Cmd+Shift+Z or Cmd+Y)
      else if ((cmdOrCtrl && e.shiftKey && e.key === 'z') || (cmdOrCtrl && e.key === 'y')) {
        e.preventDefault();
        handleRedo();
      }
    };
    
    // Focus container on mount
    if (containerRef.current) {
      containerRef.current.focus();
    }
    
    // Use capture phase to handle events before they bubble up to global handlers
    console.log('[OutlinerReact] Attaching keyboard listener to window');
    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      console.log('[OutlinerReact] Removing keyboard listener from window');
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [config.keyboardNavigation, editingCell]);
  
  if (!state) return <div>Loading...</div>;
  
  const core = coreRef.current;
  const { visibleNodes, selection, columns } = state;
  
  /**
   * Handle row click
   */
  const handleRowClick = (e, node) => {
    const id = node[core.config.idField];
    
    if (e.shiftKey) {
      core.select(id, { range: true });
    } else if (e.metaKey || e.ctrlKey) {
      core.select(id, { toggle: true });
    } else {
      core.select(id);
    }
  };
  
  /**
   * Handle disclosure click
   */
  const handleDisclosureClick = (e, node) => {
    e.stopPropagation();
    const id = node[core.config.idField];
    core.toggleExpanded(id);
  };
  
  /**
   * Handle cell double-click
   */
  const handleCellDoubleClick = (node, column) => {
    if (!column.editable) return;
    
    setEditingCell({
      nodeId: node[core.config.idField],
      columnId: column.id
    });
  };
  
  /**
   * Handle cell edit save
   */
  const handleCellSave = (nodeId, columnId, value) => {
    // Prevent double-save
    if (!editingCell) return;
    
    const column = core.config.columns.find(c => c.id === columnId);
    
    if (column) {
      // Use callback if provided (for external data management)
      if (core.config.onSave) {
        core.config.onSave(nodeId, columnId, value);
      } else {
        // Otherwise update internal data
        const node = core.getItem(nodeId);
        if (node) {
          node[column.field] = value;
          if (onDataChange) {
            onDataChange(core.getData());
          }
        }
      }
    }
    
    setEditingCell(null);
    
    // Refocus container after editing (with small delay to ensure input is unmounted)
    setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.focus();
      }
    }, 0);
  };
  
  /**
   * Render cell content
   */
  const renderCell = (node, column, isPrimary = false) => {
    const id = node[core.config.idField];
    const value = core.getFieldValue(node, column.field);
    const isEditing = editingCell?.nodeId === id && editingCell?.columnId === column.id;
    
    // Check if this is the primary column (shows tree structure)
    const showTree = isPrimary && core.config.displayMode !== DISPLAY_MODES.TABLE;
    
    return (
      <div 
        className={`outliner-cell ${column.align}`}
        style={{ 
          paddingLeft: showTree ? `${node._level * core.config.indentSize}px` : undefined 
        }}
        onDoubleClick={() => handleCellDoubleClick(node, column)}
      >
        {showTree && core.config.showDisclosure && (
          <button
            className={`disclosure-button ${node._hasChildren ? 'has-children' : ''} ${node._isExpanded ? 'expanded' : ''}`}
            onClick={(e) => handleDisclosureClick(e, node)}
            style={{ visibility: node._hasChildren ? 'visible' : 'hidden' }}
          >
            {node._hasChildren && (node._isExpanded ? '▼' : '▶')}
          </button>
        )}
        
        {isEditing ? (
          <input
            type="text"
            defaultValue={value}
            autoFocus
            onBlur={(e) => handleCellSave(id, column.id, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleCellSave(id, column.id, e.target.value);
              } else if (e.key === 'Escape') {
                e.preventDefault();
                setEditingCell(null);
                // Refocus container
                setTimeout(() => {
                  if (containerRef.current) {
                    containerRef.current.focus();
                  }
                }, 0);
              }
            }}
            className="cell-input"
          />
        ) : (
          <span className="cell-content">
            {column.render ? column.render(value, node) : (value || '')}
          </span>
        )}
      </div>
    );
  };
  
  /**
   * Render header
   */
  const renderHeader = () => {
    if (!core.config.showHeader) return null;
    
    return (
      <div className="outliner-header">
        <div className="outliner-row header-row">
          {core.config.showCheckboxes && (
            <div className="outliner-cell checkbox-cell">
              <input type="checkbox" />
            </div>
          )}
          
          {columns.order.map(columnId => {
            const column = core.config.columns.find(c => c.id === columnId);
            if (!column || !column.visible) return null;
            
            return (
              <div
                key={column.id}
                className="outliner-cell header-cell"
                style={{ width: columns.widths[column.id] }}
              >
                <span>{column.label}</span>
                {column.sortable && (
                  <button
                    className="sort-button"
                    onClick={() => {
                      const currentSort = state.sort.find(s => s.column === column.id);
                      const newDirection = currentSort?.direction === 'asc' ? 'desc' : 'asc';
                      core.sort(column.id, newDirection);
                    }}
                  >
                    ↕
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };
  
  /**
   * Render body
   */
  const renderBody = () => {
    return (
      <div className="outliner-body">
        {visibleNodes.map((node, index) => {
          const id = node[core.config.idField];
          const isSelected = selection.ids.includes(id);
          
          return (
            <div
              key={id}
              className={`outliner-row ${isSelected ? 'selected' : ''}`}
              onClick={(e) => handleRowClick(e, node)}
              data-node-id={id}
              data-level={node._level}
            >
              {core.config.showCheckboxes && (
                <div className="outliner-cell checkbox-cell">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => core.select(id, { toggle: true })}
                  />
                </div>
              )}
              
              {columns.order.map((columnId, colIndex) => {
                const column = core.config.columns.find(c => c.id === columnId);
                if (!column || !column.visible) return null;
                
                const isPrimary = column.id === core.config.primaryColumn;
                
                return (
                  <div
                    key={column.id}
                    className="outliner-cell-wrapper"
                    style={{ width: columns.widths[column.id] }}
                  >
                    {renderCell(node, column, isPrimary)}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  // Main component return
  return (
    <div ref={containerRef} className="outliner-container" tabIndex={0}>
      {renderHeader()}
      {renderBody()}
    </div>
  );
};

export default Outliner;
